import React, { useEffect, useState } from "react";
import { api, formatIDR, formatDate, errMsg } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { User, Search, Sparkles, TrendingUp, Plus, Pencil, Trash2, Phone, MapPin } from "lucide-react";

const empty = { name: "", phone: "", address: "", notes: "" };

export default function Customers() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [tab, setTab] = useState("all");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = () => api.get("/customers").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.name.trim()) return toast.error("Nama wajib diisi");
    try {
      if (editing?.id) await api.put(`/customers/${editing.id}`, form);
      else await api.post("/customers", form);
      toast.success("Konsumen tersimpan");
      setOpen(false); load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const del = async (id) => {
    if (!window.confirm("Hapus data konsumen? Histori penjualan tetap ada.")) return;
    try { await api.delete(`/customers/${id}`); toast.success("Dihapus"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (c) => { setEditing(c); setForm({ name: c.name, phone: c.phone || "", address: c.address || "", notes: c.notes || "" }); setOpen(true); };

  const filtered = items.filter((c) => {
    if (tab === "registered" && !c.registered) return false;
    if (tab === "guest" && c.registered) return false;
    if (tab === "loyal" && c.order_count < 3) return false;
    const s = q.toLowerCase();
    return !s || c.name.toLowerCase().includes(s) || (c.phone || "").includes(s);
  });

  const totalOrders = items.reduce((s, x) => s + x.order_count, 0);
  const totalRevenue = items.reduce((s, x) => s + Number(x.total_spent), 0);
  const repeatCustomers = items.filter((c) => c.order_count > 1).length;
  const registeredCount = items.filter((c) => c.registered).length;

  return (
    <>
      <PageHeader
        title="Konsumen"
        subtitle="Master data konsumen + kecenderungan pembelian dari histori"
        testId="page-customers"
        actions={
          <Button onClick={openNew} data-testid="btn-add-customer" className="bg-[var(--hotto-primary)] hover:bg-[var(--hotto-primary-hover)]">
            <Plus className="w-4 h-4 mr-2"/> Tambah Konsumen
          </Button>
        }
      />

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card className="p-5 border border-[var(--hotto-border)] rounded-md stat-card">
          <div className="overline mb-2">Total Konsumen</div>
          <div className="font-mono-num text-2xl font-bold">{items.length}</div>
          <div className="text-xs text-slate-500 mt-1">{registeredCount} terdaftar</div>
        </Card>
        <Card className="p-5 border border-[var(--hotto-border)] rounded-md stat-card">
          <div className="overline mb-2">Konsumen Repeat</div>
          <div className="font-mono-num text-2xl font-bold text-[var(--hotto-primary)]">{repeatCustomers}</div>
          <div className="text-xs text-slate-500 mt-1">≥2 kali order</div>
        </Card>
        <Card className="p-5 border border-[var(--hotto-border)] rounded-md stat-card">
          <div className="overline mb-2">Total Transaksi</div>
          <div className="font-mono-num text-2xl font-bold">{totalOrders}</div>
        </Card>
        <Card className="p-5 border border-[var(--hotto-border)] rounded-md stat-card">
          <div className="overline mb-2">Total Nilai</div>
          <div className="font-mono-num text-2xl font-bold text-[var(--hotto-success)]">{formatIDR(totalRevenue)}</div>
        </Card>
      </div>

      <div className="flex items-center justify-between gap-4 mb-4">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="all" data-testid="tab-all">Semua ({items.length})</TabsTrigger>
            <TabsTrigger value="registered" data-testid="tab-registered">Terdaftar ({registeredCount})</TabsTrigger>
            <TabsTrigger value="guest" data-testid="tab-guest">Belum Terdaftar ({items.length - registeredCount})</TabsTrigger>
            <TabsTrigger value="loyal" data-testid="tab-loyal">Pelanggan Setia</TabsTrigger>
          </TabsList>
        </Tabs>
        <div className="relative w-64">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
          <Input placeholder="Cari nama atau HP..." value={q} onChange={(e)=>setQ(e.target.value)} data-testid="input-search-customer" className="pl-9"/>
        </div>
      </div>

      <Card className="border border-[var(--hotto-border)] rounded-md overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead className="w-10">#</TableHead>
              <TableHead>Konsumen</TableHead>
              <TableHead>Kontak</TableHead>
              <TableHead className="text-right">Order</TableHead>
              <TableHead className="text-right">Total Belanja</TableHead>
              <TableHead>Order Terakhir</TableHead>
              <TableHead>Sering Beli</TableHead>
              <TableHead className="w-24"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.length === 0 && <TableRow><TableCell colSpan={8} className="text-center py-8 text-slate-500">Belum ada data konsumen.</TableCell></TableRow>}
            {filtered.map((c, idx) => (
              <TableRow key={c.name} className="table-row-hover" data-testid={`customer-row-${c.name}`}>
                <TableCell className="font-mono-num text-slate-400 text-sm">{idx + 1}</TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${c.registered?"bg-emerald-50":"bg-slate-100"}`}>
                      <User className={`w-4 h-4 ${c.registered?"text-[var(--hotto-primary)]":"text-slate-500"}`}/>
                    </div>
                    <div>
                      <div className="font-medium flex items-center gap-2">
                        {c.name}
                        {!c.registered && <Badge variant="outline" className="text-[10px] py-0 h-4">Belum terdaftar</Badge>}
                      </div>
                      {c.order_count > 3 && <div className="text-xs text-orange-600 flex items-center gap-1"><TrendingUp className="w-3 h-3"/> Pelanggan Setia</div>}
                    </div>
                  </div>
                </TableCell>
                <TableCell className="text-sm">
                  {c.phone && <div className="flex items-center gap-1 text-slate-700"><Phone className="w-3 h-3 text-slate-400"/>{c.phone}</div>}
                  {c.address && <div className="flex items-start gap-1 text-slate-500 text-xs mt-0.5"><MapPin className="w-3 h-3 mt-0.5 shrink-0"/>{c.address.slice(0,40)}{c.address.length>40?"...":""}</div>}
                  {!c.phone && !c.address && <span className="text-xs text-slate-400">-</span>}
                </TableCell>
                <TableCell className="text-right font-mono-num">
                  <Badge variant={c.order_count > 1 ? "default" : "outline"} className={c.order_count > 1 ? "bg-[var(--hotto-primary)]" : ""}>{c.order_count}x</Badge>
                </TableCell>
                <TableCell className="text-right font-mono-num font-semibold text-[var(--hotto-success)]">{formatIDR(c.total_spent)}</TableCell>
                <TableCell className="text-sm text-slate-600">{c.last_order ? formatDate(c.last_order) : "-"}</TableCell>
                <TableCell>
                  <div className="flex flex-wrap gap-1">
                    {c.favorites.slice(0, 3).map((f) => (
                      <Badge key={f.product_id} variant="outline" className="text-xs">
                        <Sparkles className="w-2.5 h-2.5 mr-1 text-orange-500"/>
                        {f.name} <span className="text-slate-500 ml-1">{f.count}x</span>
                      </Badge>
                    ))}
                    {c.favorites.length === 0 && <span className="text-xs text-slate-400">-</span>}
                  </div>
                </TableCell>
                <TableCell className="text-right">
                  <Button size="icon" variant="ghost" onClick={()=>openEdit(c)} data-testid={`btn-edit-${c.name}`}><Pencil className="w-4 h-4"/></Button>
                  {c.id && <Button size="icon" variant="ghost" onClick={()=>del(c.id)}><Trash2 className="w-4 h-4 text-red-500"/></Button>}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing ? "Edit Konsumen" : "Tambah Konsumen"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Nama</Label><Input value={form.name} onChange={(e)=>setForm({...form,name:e.target.value})} data-testid="input-customer-name"/></div>
            <div><Label>No. HP / WhatsApp</Label><Input value={form.phone} onChange={(e)=>setForm({...form,phone:e.target.value})} placeholder="08xxx..." data-testid="input-customer-phone"/></div>
            <div><Label>Alamat</Label><Textarea value={form.address} onChange={(e)=>setForm({...form,address:e.target.value})} data-testid="input-customer-address"/></div>
            <div><Label>Catatan (preferensi, ulang tahun, dll)</Label><Textarea value={form.notes} onChange={(e)=>setForm({...form,notes:e.target.value})}/></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setOpen(false)}>Batal</Button>
            <Button onClick={save} className="bg-[var(--hotto-primary)] hover:bg-[var(--hotto-primary-hover)]" data-testid="btn-save-customer">Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
